/**
 * What each Deal Room section is, and why it is there.
 *
 * This file is CONTENT, held apart from the components on purpose: it is the product explaining itself
 * to a client, so it should be reviewable as writing rather than hunted through JSX. Chad or Brad can
 * read this one file and know exactly what the app says about itself.
 *
 * ## The rules the copy follows
 *
 * **Two beats, in order: what this is, then why it matters to you.** A line that only says what a
 * section contains ("the sensitivity analysis") tells a reader nothing they could not get from the tab
 * label. The second beat is the whole point of the hint.
 *
 * **Canon's voice (Brand Guidelines v1.4 §03): adult-to-adult, plain and grounded, systems not
 * heroes.** The reader is a capable professional. No selling, no adjectives doing work that facts
 * should do, and — canon §4.5 — the machine's output is a recommendation carrying a human decision
 * point, never a verdict handed down.
 *
 * **Short enough to actually read.** Two sentences. A paragraph in a popover is a paragraph nobody
 * reads, and it covers the thing the reader was looking at.
 */

export const SECTION_HELP: Record<string, string> = {
  overview:
    'The recommendation, the score behind it, and the metrics a committee asks for first. If you read one screen of this deal, read this one.',
  findings:
    'Where the score came from — each category, what it scored, and how much of the gap is fixable rather than structural. If you think one is wrong, challenge it here.',
  sensitivity:
    'What happens to the return if rents come in soft or the exit cap moves against you. The base case is one cell in this grid, not a promise.',
  lender:
    'The tests a lender applies before committing: debt coverage, loan-to-cost, debt yield. This is whether the deal clears them as underwritten today.',
  construction:
    "What the contractor's estimate holds, division by division, and how those lines add up to the guaranteed maximum price. It shows where the money in this job sits, and how much of the price is loading rather than construction.",
  levers:
    'What would actually move this deal — each change priced in basis points of yield and project return, against the gap to your target. A lever with no number was still tested; the status says why it came back empty.',
  sponsor:
    'Execution risk carried by the team delivering this project, and the memo you would take into committee. A sound deal with the wrong builder still ends badly.',
  sources:
    'Which documents this underwriting read, what it took from each, and anything it could not read. Every number on this deal traces back to here.',
  challenges:
    'Every conclusion here is challengeable by name. File one against a finding, and an accepted correction re-scores the deal in the open — the old score and the new one both stay on the record.',
};

/**
 * The hint for a section, or `undefined` when there is nothing worth saying. Exported and total so a
 * new section cannot silently ship with a stale or missing explanation — the test enumerates the
 * section ids the report builds and asserts every one is covered.
 */
export function helpFor(sectionId: string): string | undefined {
  return SECTION_HELP[sectionId];
}
